import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type {
  AppState, Habit, HabitLog, HealthKind, Settings, StarReason, Task, FocusSession, Source,
} from "@soheil/core";
import { productDay } from "@soheil/core";
import { gregorianToJalali } from "@soheil/core";
import { STAR_VALUES, canAward, levelForStars, streakBonus } from "@soheil/core";
import { applyActivity, refillFreeze } from "@soheil/core";
import { studiedMinutes } from "@soheil/core";
import { makeRepo, type Repo } from "./repo";
import { uid } from "./seed";
import { rawInitData } from "../platform/telegram";

/* ────────────────────────── actions ────────────────────────── */

export type Action =
  | { type: "hydrate"; state: AppState }
  | { type: "toggleHabit"; habitId: string; day: string; neverZero?: boolean }
  | { type: "addHabit"; habit: Omit<Habit, "id" | "createdAt" | "updatedAt" | "sortOrder"> }
  | { type: "updateHabit"; id: string; patch: Partial<Habit> }
  | { type: "removeHabit"; id: string }
  | { type: "addTask"; title: string; dueDate?: string; domain?: string; isTopThree?: boolean }
  | { type: "toggleTask"; id: string; day: string }
  | { type: "updateTask"; id: string; patch: Partial<Task> }
  | { type: "removeTask"; id: string }
  | { type: "addMind"; text: string }
  | { type: "processMind"; id: string; asTask?: boolean }
  | { type: "removeMind"; id: string }
  | { type: "restoreMind"; id: string }
  | { type: "logHealth"; kind: HealthKind; day: string; value: number; meta?: Record<string, unknown> }
  | { type: "removeHealth"; id: string }
  | { type: "removeLastExercise"; day: string }
  | { type: "startSession"; durationSec: number; label?: string; taskId?: string }
  | { type: "patchSession"; session: FocusSession; day: string }
  | { type: "setSettings"; patch: Partial<Settings> }
  | { type: "saveReview"; day: string; mood: 1 | 2 | 3 | 4 | 5; bestWin?: string; tomorrowTop?: string }
  | { type: "reset" };

/* ─────────────────────── star + streak engine ─────────────────────── */

const SOURCE: Source = "miniapp";

/** تنها جایی که ستاره اضافه می‌شود — سقف‌ها و سطح همیشه اینجا اعمال می‌شوند. */
function award(state: AppState, reason: StarReason, day: string, refId?: string, override?: number): AppState {
  if (!canAward(state.stars, reason, day)) return state;
  const delta = override ?? STAR_VALUES[reason];
  if (delta === 0) return state;
  const stars = [...state.stars, { id: uid(), delta, reason, localDate: day, createdAt: Date.now(), refId }];
  const totalStars = state.progress.totalStars + delta;
  return {
    ...state,
    stars,
    progress: { ...state.progress, totalStars, level: levelForStars(totalStars) },
  };
}

/** هر فعالیتی در یک روز، زنجیره را زنده نگه می‌دارد و ممکن است پاداش هفتگی بدهد. */
function touchActivity(state: AppState, day: string): AppState {
  const [jy, jm] = gregorianToJalali(...(day.split("-").map(Number) as [number, number, number]));
  // طرح از سرور روی state نشسته؛ سرور هم موقع نوشتن سقف را می‌بُرد،
  // پس کلاینت دست‌کاری‌شده نمی‌تواند برای خودش توکن بسازد.
  const plan = (state as { __plan?: string }).__plan === "plus" ? "plus" : "free";
  let progress = refillFreeze(state.progress, `${jy}-${String(jm).padStart(2, "0")}`, plan);

  const r = applyActivity(progress, day, {
    vacationFrom: state.settings?.vacationFrom,
    vacationUntil: state.settings?.vacationUntil,
  });
  progress = {
    ...progress,
    currentStreak: r.currentStreak,
    longestStreak: r.longestStreak,
    freezeTokens: r.freezeTokens,
    lastActiveDay: day,
  };

  let next: AppState = { ...state, progress };
  const bonus = streakBonus(r.currentStreak);
  if (bonus > 0) next = award(next, "streak_bonus", day, undefined, bonus);
  return next;
}

/** آیا همهٔ عادت‌های برنامه‌ریزی‌شدهٔ امروز انجام شده‌اند؟ */
function checkFullDay(state: AppState, day: string): AppState {
  const active = state.habits.filter((h) => !h.archived && !h.deletedAt);
  if (active.length === 0) return state;
  const done = active.every((h) => state.habitLogs.some((l) => l.habitId === h.id && l.localDate === day));
  return done ? award(state, "full_day", day) : state;
}

/* ────────────────────────── reducer ────────────────────────── */

function reducer(state: AppState, action: Action): AppState {
  const now = Date.now();

  switch (action.type) {
    case "hydrate":
      return action.state;

    case "toggleHabit": {
      const { habitId, day, neverZero = false } = action;
      const existing = state.habitLogs.find((l) => l.habitId === habitId && l.localDate === day);
      if (existing) {
        // برداشتن تیک — ستاره پس گرفته نمی‌شود؛ دفتر ستاره فقط جلو می‌رود
        return { ...state, habitLogs: state.habitLogs.filter((l) => l !== existing) };
      }
      const log: HabitLog = { habitId, localDate: day, value: 1, neverZero, completedAt: now, source: SOURCE };
      let next: AppState = { ...state, habitLogs: [...state.habitLogs, log] };
      next = award(next, neverZero ? "habit_never_zero" : "habit_done", day, habitId);
      next = touchActivity(next, day);
      next = checkFullDay(next, day);
      return next;
    }

    case "addHabit": {
      const order = Math.max(-1, ...state.habits.map((h) => h.sortOrder)) + 1;
      const habit: Habit = { ...action.habit, id: uid(), sortOrder: order, createdAt: now, updatedAt: now };
      return { ...state, habits: [...state.habits, habit] };
    }

    case "updateHabit":
      return {
        ...state,
        habits: state.habits.map((h) => (h.id === action.id ? { ...h, ...action.patch, updatedAt: now } : h)),
      };

    case "removeHabit":
      return {
        ...state,
        habits: state.habits.filter((h) => h.id !== action.id),
        habitLogs: state.habitLogs.filter((l) => l.habitId !== action.id),
      };

    case "addTask": {
      const topCount = state.tasks.filter((t) => t.isTopThree && !t.completedAt && !t.deletedAt).length;
      const task: Task = {
        id: uid(), title: action.title, dueDate: action.dueDate, domain: action.domain,
        priority: 1, isTopThree: action.isTopThree ?? topCount < 3,
        createdAt: now, updatedAt: now,
      };
      return { ...state, tasks: [...state.tasks, task] };
    }

    case "toggleTask": {
      const t = state.tasks.find((x) => x.id === action.id);
      if (!t) return state;
      const completing = !t.completedAt;
      const tasks = state.tasks.map((x) =>
        x.id === action.id ? { ...x, completedAt: completing ? now : undefined, updatedAt: now } : x,
      );
      let next: AppState = { ...state, tasks };
      if (completing) {
        next = award(next, "task_done", action.day, t.id);
        next = touchActivity(next, action.day);
      }
      return next;
    }

    case "updateTask":
      return {
        ...state,
        tasks: state.tasks.map((t) => (t.id === action.id ? { ...t, ...action.patch, updatedAt: now } : t)),
      };

    case "removeTask":
      return { ...state, tasks: state.tasks.filter((t) => t.id !== action.id) };

    case "addMind":
      return { ...state, mind: [{ id: uid(), text: action.text, createdAt: now }, ...state.mind] };

    case "processMind": {
      const item = state.mind.find((m) => m.id === action.id);
      if (!item) return state;
      const mind = state.mind.map((m) => (m.id === action.id ? { ...m, processedAt: now } : m));
      if (!action.asTask) return { ...state, mind };
      const topCount = state.tasks.filter((t) => t.isTopThree && !t.completedAt).length;
      const task: Task = {
        id: uid(), title: item.text, priority: 1, isTopThree: topCount < 3,
        createdAt: now, updatedAt: now,
      };
      return { ...state, mind, tasks: [...state.tasks, task] };
    }

    case "restoreMind": {
      // برگرداندن از «کارهای امروز» به «ذهنم» — همان کاری که دکمهٔ برگردان می‌کند
      const item = state.mind.find((m) => m.id === action.id);
      if (!item) return state;
      const tasks = state.tasks.filter((t) => t.title !== item.text || t.completedAt);
      return {
        ...state,
        tasks,
        mind: state.mind.map((m) => (m.id === action.id ? { ...m, processedAt: undefined } : m)),
      };
    }

    case "removeMind":
      return { ...state, mind: state.mind.filter((m) => m.id !== action.id) };

    case "removeLastExercise": {
      const mine = state.health.filter((h) => h.kind === "exercise" && h.localDate === action.day);
      const last = mine[mine.length - 1];
      if (!last) return state;
      return { ...state, health: state.health.filter((h) => h !== last) };
    }

    case "removeHealth":
      // ستاره پس گرفته نمی‌شود — دفتر ستاره فقط جلو می‌رود.
      // ولی رکورد سلامتی باید قابل حذف باشد وگرنه یک اشتباه تا ابد می‌ماند.
      return { ...state, health: state.health.filter((h) => h.id !== action.id) };

    case "logHealth": {
      const { kind, day, value, meta } = action;
      let health = state.health;
      if (kind === "water") {
        const existing = health.find((h) => h.kind === "water" && h.localDate === day);
        health = existing
          ? health.map((h) => (h === existing ? { ...h, value, loggedAt: now } : h))
          : [...health, { id: uid(), kind, localDate: day, value, loggedAt: now, source: SOURCE }];
      } else if (kind === "sleep") {
        // خواب شبی یک بار است؛ ثبت دوم یعنی تصحیح اولی، نه خوابیدن دوباره
        const existing = health.find((h) => h.kind === "sleep" && h.localDate === day);
        health = existing
          ? health.map((h) => (h === existing ? { ...h, value, meta, loggedAt: now } : h))
          : [...health, { id: uid(), kind, localDate: day, value, meta, loggedAt: now, source: SOURCE }];
      } else {
        // ورزش می‌تواند چند جلسه در روز باشد، پس انباشته می‌شود
        health = [...health, { id: uid(), kind, localDate: day, value, meta, loggedAt: now, source: SOURCE }];
      }

      let next: AppState = { ...state, health };
      if (kind === "water" && value >= state.settings.waterGoal) next = award(next, "water_goal", day);
      if (kind === "exercise") next = award(next, "exercise_logged", day);
      if (kind === "sleep") next = award(next, "sleep_logged", day);
      return touchActivity(next, day);
    }

    case "startSession": {
      const s: FocusSession = {
        id: uid(), startedAt: now, durationSec: action.durationSec,
        pausedMs: 0, label: action.label, taskId: action.taskId,
      };
      return { ...state, sessions: [...state.sessions, s] };
    }

    case "patchSession": {
      const prev = state.sessions.find((s) => s.id === action.session.id);
      const sessions = state.sessions.map((s) => (s.id === action.session.id ? action.session : s));
      let next: AppState = { ...state, sessions };

      // تازه کامل شد — ستاره و دقیقهٔ مطالعه فقط یک بار حساب می‌شوند
      if (action.session.completedAt && !prev?.completedAt) {
        const mins = studiedMinutes(action.session);
        next = { ...next, progress: { ...next.progress, studyMinutes: next.progress.studyMinutes + mins } };
        next = award(next, "focus_complete", action.day, action.session.id);
        next = touchActivity(next, action.day);
      }
      return next;
    }

    case "setSettings":
      return { ...state, settings: { ...state.settings, ...action.patch } };

    case "saveReview": {
      const reviews = state.reviews.filter((r) => r.localDate !== action.day);
      return {
        ...state,
        reviews: [...reviews, {
          localDate: action.day, mood: action.mood,
          bestWin: action.bestWin, tomorrowTop: action.tomorrowTop, createdAt: now,
        }],
      };
    }

    case "reset":
      return state;
  }
}

/* ────────────────────────── provider ────────────────────────── */

interface Ctx {
  state: AppState;
  dispatch: React.Dispatch<Action>;
  today: string;
  ready: boolean;
}

const StoreCtx = createContext<Ctx | null>(null);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, null as unknown as AppState, () => ({} as AppState));
  const [ready, setReady] = useState(false);
  const [today, setToday] = useState(() => productDay());
  const repo = useRef<Repo>(makeRepo(rawInitData()));
  const saveTimer = useRef<number | undefined>(undefined);
  // آخرین state، بدون اینکه افکت سینک به هر تغییر وابسته شود
  const latest = useRef<AppState>(state);
  latest.current = state;

  // بارگذاری اولیه
  useEffect(() => {
    let alive = true;
    void (async () => {
      const loaded = await repo.current.load();
      if (!alive) return;
      dispatch({ type: "hydrate", state: loaded });
      setReady(true);
    })();
    return () => { alive = false; };
  }, []);

  // ذخیرهٔ با تأخیر — هر کلیک یک نوشتن کامل نمی‌خواهد
  useEffect(() => {
    if (!ready) return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => { void repo.current.save(state); }, 400);
    return () => window.clearTimeout(saveTimer.current);
  }, [state, ready]);

  // گرفتن تغییرات از سرور: موقع برگشتن به اپ و هر دقیقه یک بار.
  // اگر کاربر در ربات چیزی ثبت کرده باشد، اپ باید همان را نشان دهد.
  useEffect(() => {
    if (!ready) return;
    const r = repo.current;
    if (!r.pull) return;
    let alive = true;
    let busy = false;
    const tick = async () => {
      if (!alive || busy || document.hidden) return;
      busy = true;
      try {
        const merged = await r.pull!(latest.current);
        if (alive && merged !== latest.current) dispatch({ type: "hydrate", state: merged });
      } catch {
        // آفلاین — دفعهٔ بعد
      } finally {
        busy = false;
      }
    };
    const id = window.setInterval(tick, 60_000);
    document.addEventListener("visibilitychange", tick);
    void tick();
    return () => {
      alive = false;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [ready]);

  // اگر تب باز بماند و از مرز ۴ صبح رد شویم، «امروز» باید عوض شود
  useEffect(() => {
    const tick = () => setToday(productDay());
    const id = window.setInterval(tick, 60_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  const value = useMemo<Ctx>(() => ({ state, dispatch, today, ready }), [state, today, ready]);
  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

export function useStore(): Ctx {
  const c = useContext(StoreCtx);
  if (!c) throw new Error("useStore باید داخل StoreProvider استفاده شود");
  return c;
}
